<template>
    <vx-table-row>
        <vx-table-row-title>
            {{ props.modelValue.group_name }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> External ID: </template>
            <span class="break-all">{{ props.modelValue.group_id }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> OU: </template>
            <vx-icon-loading v-if="loadingOu" spin></vx-icon-loading>
            <vx-popover v-if="ouName" hover :arrow="true" placement="top">
                <font-awesome-icon widthAuto :icon="faBuildingFlag"></font-awesome-icon>
                <template v-slot:content>
                    <span class="text-sm font-normal">{{ ouName }}</span>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Users: </template>
            <vx-icon-loading v-if="loadingUsers" spin></vx-icon-loading>
            <span v-else>{{ usersCount }}</span>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button data-test="remove" type="button" color="muted" outline @click="removeGroup()">
                Remove <font-awesome-icon widthAuto :icon="faCircleMinus" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup lang="ts">
import { faBuildingFlag, faCircleMinus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxIconLoading,
    VxPopover,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { ref, onMounted } from 'vue';
import { groupsClient, groupUsersClient } from '~/services';
import type { MessageStandardTargetGroupType } from '~/services/messageStandards/messageStandardsClient.types';

const props = defineProps<{ modelValue: MessageStandardTargetGroupType; messageStandardId: number }>();

const emit = defineEmits(['remove']);

const ouName = ref('');
const loadingOu = ref(false);
const usersCount = ref('0');
const loadingUsers = ref(true);

const fetchGroupOu = async () => {
    if (ouName.value || loadingOu.value) {
        return;
    }

    try {
        const { data } = await groupsClient.getGroup({ teamId: props.modelValue.team_id }, props.modelValue.group_id);
        ouName.value = data.org_unit?.name || '';
    } catch {
        ouName.value = '';
    } finally {
        loadingOu.value = false;
    }
};

const fetchUsersCount = async () => {
    try {
        const { data } = await groupUsersClient.countGroupUsers(props.modelValue.team_id, props.modelValue.group_id);
        usersCount.value = Intl.NumberFormat('en-US').format(data.count);
    } catch {
        usersCount.value = '0';
    } finally {
        loadingUsers.value = false;
    }
};

const removeGroup = async () =>
    VxSwal.confirmation('Remove Group from Brand Standard', 'Are you sure? There is no undo.', 'danger', async () => {
        emit('remove', props.modelValue.group_id);
    });

onMounted(() => {
    fetchUsersCount();
    fetchGroupOu();
});
</script>
